// Collection/search filtering. Deferred after theme.js, which owns the shared element bases and event helpers.
(() => {
  if (customElements.get("product-facet")) return;
  const readGridMode = () => {
    try { return localStorage.getItem("gridMode") === "compact" ? "compact" : "default"; }
    catch (_) { return "default"; }
  };
  const syncSort = (facet, value) => {
    facet?.querySelectorAll('[name="sort_by"]').forEach(input => {
      if (input.type === "radio") input.checked = input.value === value;
      else input.value = value;
    });
  };
  const filterUrl = form => {
    const url = new URL(location.href);
    [...url.searchParams.keys()].filter(key => key.startsWith("filter.") || key === "page").forEach(key => url.searchParams.delete(key));
    for (const [key, value] of new FormData(form)) {
      if (key.startsWith("filter.")) url.searchParams.append(key, value);
      else url.searchParams.set(key, value);
    }
    return url.toString();
  };

// js/custom-element/section/product-facet/product-facet.js
var ProductFacet = class extends CustomHTMLElement {
  connectedCallback() {
    this._renderedUrl = location.href;
    this._pagePath = location.pathname;
    this.lifecycle = new AbortController();
    window.addEventListener("popstate", () => {
      if (location.pathname === this._pagePath && location.href !== this._renderedUrl) {
        this._render(location.href, { updateHistory: false });
      }
    }, { signal: this.lifecycle.signal });
    this.delegate.on("click", '[data-action="clear-filters"]', (event, target) => {
      event.preventDefault();
      this._rerender({ detail: { url: target.href } });
    });
    this.delegate.on("pagination:page-changed", this._rerender.bind(this));
    this.delegate.on("facet:criteria-changed", this._rerender.bind(this));
    this.delegate.on("facet:abort-loading", this._abort.bind(this));
  }
  disconnectedCallback() {
    this.lifecycle?.abort();
    this._abort();
    super.disconnectedCallback();
  }
  _rerender(event) {
    return this._render(event.detail.url, { focusResults: event.type === "pagination:page-changed" });
  }
  async _render(target, { updateHistory = true, focusResults = false } = {}) {
    const pageUrl = new URL(target, location.href);
    if (pageUrl.origin !== location.origin) return;
    if (updateHistory && pageUrl.pathname === location.pathname) {
      const current = new URL(location.href);
      for (const key of new Set(current.searchParams.keys())) {
        if (!key.startsWith("filter.") && key !== "page" && key !== "section_id" && !pageUrl.searchParams.has(key)) {
          current.searchParams.getAll(key).forEach(value => pageUrl.searchParams.append(key, value));
        }
      }
    }
    pageUrl.searchParams.delete("section_id");
    if (this.closest("search-page")) pageUrl.searchParams.set("type", "product");
    this._abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.querySelector("[data-facet-error]")?.remove();
    this.querySelector("#facet-main")?.setAttribute("aria-busy", "true");
    this.showLoadingBar();
    const url = new URL(pageUrl);
    url.searchParams.set("section_id", this.getAttribute("section-id"));
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error("Filter request failed");
      const responseAsText = await response.text();
      if (controller.signal.aborted || controller !== this.abortController || !this.isConnected) return;
      const fakeDiv = document.createElement("div");
      fakeDiv.innerHTML = responseAsText;
      if (!fakeDiv.querySelector("#facet-main")) throw new Error("Missing filter results");
      const focusedElement = this.contains(document.activeElement) ? document.activeElement : null;
      const focusedId = focusedElement?.id;
      this.querySelector("#facet-main").innerHTML = fakeDiv.querySelector("#facet-main").innerHTML;

      // Update mobile toolbar filter button and active filters list
      const newMobileToolbar = fakeDiv.querySelector("#mobile-facet-toolbar");
      const currentMobileToolbar = this.querySelector("#mobile-facet-toolbar");

      if (newMobileToolbar && currentMobileToolbar) {
        const newFilterButton = newMobileToolbar.querySelector(".mobile-toolbar__item--filters");
        const currentFilterButton = currentMobileToolbar.querySelector(".mobile-toolbar__item--filters");

        if (newFilterButton && currentFilterButton) {
          // Update the label text with new count
          const newLabel = newFilterButton.querySelector(".mobile-toolbar__item-label");
          const currentLabel = currentFilterButton.querySelector(".mobile-toolbar__item-label");
          if (newLabel && currentLabel) {
            currentLabel.textContent = newLabel.textContent;
          }
        }

        // Update mobile active filters list
        const newMobileActiveFilters = fakeDiv.querySelector(".product-facet__active-list.hidden-tablet-and-up");
        const currentMobileActiveFilters = this.querySelector(".product-facet__active-list.hidden-tablet-and-up");

        if (newMobileActiveFilters && currentMobileActiveFilters) {
          currentMobileActiveFilters.innerHTML = newMobileActiveFilters.innerHTML;
        } else if (newMobileActiveFilters && !currentMobileActiveFilters) {
          // Add active filters if they don't exist
          currentMobileToolbar.insertAdjacentElement('afterend', newMobileActiveFilters.cloneNode(true));
        } else if (!newMobileActiveFilters && currentMobileActiveFilters) {
          // Remove active filters if there are none
          currentMobileActiveFilters.remove();
        }
      }

      // Update mobile drawer filters
      const filtersTempDiv = fakeDiv.querySelector("#facet-filters");
      if (filtersTempDiv) {
        const mobileFilters = this.querySelector("#facet-filters");
        if (mobileFilters) {
          const previousScrollTop = mobileFilters.querySelector(".drawer__content").scrollTop;
          Array.from(this.querySelectorAll("#facet-filters-form .collapsible-toggle[aria-controls]")).forEach((filterToggle) => {
            const filtersTempDivToggle = filtersTempDiv.querySelector(`[aria-controls="${filterToggle.getAttribute("aria-controls")}"]`), isExpanded = filterToggle.getAttribute("aria-expanded") === "true";
            if (filtersTempDivToggle) {
              filtersTempDivToggle.setAttribute("aria-expanded", isExpanded ? "true" : "false");
              filtersTempDivToggle.nextElementSibling.toggleAttribute("open", isExpanded);
              filtersTempDivToggle.nextElementSibling.style.overflow = isExpanded ? "visible" : "";
            }
          });
          mobileFilters.innerHTML = filtersTempDiv.innerHTML;
          mobileFilters.querySelector(".drawer__content").scrollTop = previousScrollTop;
        }
      }

      // Desktop filter bar is now OUTSIDE #facet-main, so it doesn't get replaced
      // We just need to update the badges and checkbox states without destroying the elements
      const desktopFiltersTempDiv = fakeDiv.querySelector("#facet-filters-bar");
      const desktopFilters = this.querySelector("#facet-filters-bar");

      if (desktopFiltersTempDiv && desktopFilters) {
        // Update only the active filter tags below the bar
        const newActiveFilters = desktopFiltersTempDiv.querySelector(".product-facet__active-list");
        const currentActiveFilters = desktopFilters.querySelector(".product-facet__active-list");

        if (newActiveFilters && currentActiveFilters) {
          currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
        } else if (newActiveFilters && !currentActiveFilters) {
          // Add active filters if they don't exist
          desktopFilters.appendChild(newActiveFilters.cloneNode(true));
        } else if (!newActiveFilters && currentActiveFilters) {
          // Remove active filters if there are none
          currentActiveFilters.remove();
        }

        // Update badges on filter buttons and checkbox states in popovers
        const newDropdowns = desktopFiltersTempDiv.querySelectorAll(".facet-filter-dropdown");
        const currentDropdowns = desktopFilters.querySelectorAll(".facet-filter-dropdown");

        newDropdowns.forEach((newDropdown, index) => {
          const currentDropdown = currentDropdowns[index];
          if (currentDropdown) {
            // Update badge
            const newButton = newDropdown.querySelector(".facet-filter-dropdown__button");
            const currentButton = currentDropdown.querySelector(".facet-filter-dropdown__button");

            if (newButton && currentButton) {
              const newBadge = newButton.querySelector(".facet-filter-dropdown__badge");
              const currentBadge = currentButton.querySelector(".facet-filter-dropdown__badge");

              if (newBadge && currentBadge) {
                currentBadge.textContent = newBadge.textContent;
              } else if (newBadge && !currentBadge) {
                // Add badge if it doesn't exist
                const label = currentButton.querySelector(".facet-filter-dropdown__label");
                if (label) {
                  label.insertAdjacentHTML('afterend', newBadge.outerHTML);
                }
              } else if (!newBadge && currentBadge) {
                // Remove badge if filter is no longer active
                currentBadge.remove();
              }
            }

            // Replace the entire popover content to handle hidden/shown filter values
            const newPopover = newDropdown.querySelector("filter-popover");
            const currentPopover = currentDropdown.querySelector("filter-popover");

            if (newPopover && currentPopover) {
              // Store the open state before replacing content
              const wasOpen = currentPopover.hasAttribute('open');

              // Replace the entire popover content
              currentPopover.innerHTML = newPopover.innerHTML;

              // Restore open state if it was open
              if (wasOpen) {
                currentPopover.setAttribute('open', '');
              }
            }
          }
        });
      }

      const newSortLabel = fakeDiv.querySelector("#sort-by-selected-value-desktop");
      const sortLabel = this.querySelector("#sort-by-selected-value-desktop");
      if (newSortLabel && sortLabel) sortLabel.textContent = newSortLabel.textContent;
      const newMobileSort = fakeDiv.querySelector("sort-by-popover");
      const mobileSort = this.querySelector("sort-by-popover");
      if (newMobileSort && mobileSort) mobileSort.innerHTML = newMobileSort.innerHTML;
      const selectedSort = fakeDiv.querySelector('input[type="hidden"][name="sort_by"]')?.value;
      if (selectedSort) syncSort(this, selectedSort);

      // Re-apply grid mode after AJAX update
      const filterBar = this.querySelector("#facet-filters-bar");
      if (filterBar && filterBar.reapplyGridMode) {
        filterBar.reapplyGridMode();
      }

      if (updateHistory && pageUrl.href !== location.href) history.pushState(history.state, "", pageUrl);
      this._renderedUrl = pageUrl.href;
      document.dispatchEvent(new CustomEvent("collection:url-changed"));
      if (focusedId) this.querySelector(`#${CSS.escape(focusedId)}`)?.focus({ preventScroll: true });
      else if (focusResults && focusedElement) {
        const main = this.querySelector("#facet-main");
        main.setAttribute("tabindex", "-1");
        main.focus({ preventScroll: true });
      }
      const hasOpenFilters = this.querySelector("filter-popover[open], facet-filters[open]");
      if (!hasOpenFilters && updateHistory) {
        const scrollTo = this.querySelector(".product-facet__meta-bar, .product-facet__product-list, .product-facet__main");
        requestAnimationFrame(() => {
          if (this.isConnected && this._renderedUrl === pageUrl.href) {
            scrollTo?.scrollIntoView({ block: "start", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
          }
        });
      }
    } catch (error) {
      if (!controller.signal.aborted && controller === this.abortController && this.isConnected) {
        const message = document.createElement("p");
        message.className = "alert alert--error";
        message.dataset.facetError = "";
        message.setAttribute("role", "alert");
        message.append(document.createTextNode(`${this.dataset.errorMessage} `));
        const retry = document.createElement("a");
        retry.className = "link";
        retry.href = pageUrl.href;
        retry.textContent = this.dataset.retryLabel;
        message.append(retry);
        this.querySelector("#facet-main")?.prepend(message);
      }
    } finally {
      if (controller === this.abortController) {
        this.abortController = null;
        this.querySelector("#facet-main")?.removeAttribute("aria-busy");
        this.hideLoadingBar();
      }
    }
  }
  _abort() {
    if (!this.abortController) return;
    this.abortController.abort();
    this.abortController = null;
    this.querySelector("#facet-main")?.removeAttribute("aria-busy");
    this.hideLoadingBar();
  }
};
customElements.define("product-facet", ProductFacet);

// js/custom-element/section/facet/facet-filters.js
// Mobile Drawer Filters
var FacetFilters = class extends DrawerContent {
  connectedCallback() {
    super.connectedCallback();
    this.delegate.on("change", '[name^="filter."]', this._onFilterChanged.bind(this));
  }
  _onFilterChanged() {
    triggerEvent(this, "facet:criteria-changed", { url: filterUrl(this.querySelector("#facet-filters-form")) });
  }
};
customElements.define("facet-filters", FacetFilters);

// Desktop Filters Bar
var FacetFiltersBar = class extends CustomHTMLElement {
  connectedCallback() {
    this.delegate.on("change", '[name^="filter."]', this._onFilterChanged.bind(this));
    this.delegate.on("change", '[name="sort_by"]', this._onSortChanged.bind(this));
    this.delegate.on("keydown", this._onEscape.bind(this));

    // Products per row toggle
    this.delegate.on("click", ".products-per-row-toggle__button", this._onGridToggle.bind(this));

    // Initialize grid mode from localStorage
    this._initializeGridMode();

    // Close popovers when clicking outside
    this.lifecycle = new AbortController();
    document.addEventListener("click", this._handleOutsideClick.bind(this), { signal: this.lifecycle.signal });
  }
  disconnectedCallback() {
    this.lifecycle?.abort();
    super.disconnectedCallback();
  }
  _onEscape(event) {
    if (event.key !== "Escape") return;
    const popover = this.querySelector("filter-popover[open]");
    if (!popover) return;
    event.preventDefault();
    event.stopPropagation();
    popover.open = false;
    this.querySelector(`[aria-controls="${CSS.escape(popover.id)}"]`)?.focus({ preventScroll: true });
  }
  _initializeGridMode() {
    // Apply saved mode if it exists
    this._setGridMode(readGridMode());
  }
  _onGridToggle(event, target) {
    const mode = target.getAttribute("data-grid-mode");
    this._setGridMode(mode);
    try { localStorage.setItem("gridMode", mode); } catch (_) {}
  }
  _setGridMode(mode) {
    mode = mode === "compact" ? "compact" : "default";
    // Update button active states
    const buttons = this.querySelectorAll(".products-per-row-toggle__button");
    buttons.forEach((btn) => {
      btn.setAttribute("aria-pressed", String(btn.getAttribute("data-grid-mode") === mode));
      if (btn.getAttribute("data-grid-mode") === mode) {
        btn.classList.add("is-active");
      } else {
        btn.classList.remove("is-active");
      }
    });

    // Apply grid mode to product list using CSS classes
    const productList = this.closest("product-facet")?.querySelector("#facet-main .product-list");
    if (productList) {
      // Remove all grid mode classes
      productList.classList.remove("product-list--grid-default", "product-list--grid-compact");

      // Add the appropriate class
      productList.classList.add(`product-list--grid-${mode}`);
    }
  }
  reapplyGridMode() {
    this._setGridMode(readGridMode());
  }
  _onFilterChanged() {
    triggerEvent(this, "facet:criteria-changed", { url: filterUrl(this.querySelector("#facet-filters-form-desktop")) });
  }
  _onSortChanged(event, target) {
    const currentUrl = new URL(location.href);
    currentUrl.searchParams.set("sort_by", target.value);
    currentUrl.searchParams.delete("page");
    syncSort(this.closest("product-facet"), target.value);

    // Close the popover
    const popover = target.closest("filter-popover");
    if (popover) {
      popover.open = false;
    }

    triggerEvent(this, "facet:criteria-changed", { url: currentUrl.toString() });
  }
  _handleOutsideClick(event) {
    // Close any open popovers when clicking outside
    const openPopovers = this.querySelectorAll("filter-popover[open]");
    openPopovers.forEach((popover) => {
      const button = this.querySelector(`[aria-controls="${popover.id}"]`);
      if (button && !button.contains(event.target) && !popover.contains(event.target)) {
        // Trigger close via the button to maintain proper state
        button.isExpanded = false;
      }
    });
  }
};
customElements.define("facet-filters-bar", FacetFiltersBar);

// Filter Popover (Desktop dropdowns)
var FilterPopover = class extends OpenableElement {
  // Override to disable focus trapping for simple dropdowns
  get shouldTrapFocus() {
    return false;
  }
};
customElements.define("filter-popover", FilterPopover);

// js/custom-element/section/facet/sort-by-popover.js
var SortByPopover = class extends PopoverContent {
  connectedCallback() {
    super.connectedCallback();
    this.delegate.on("change", '[name="sort_by"]', this._onSortChanged.bind(this));
  }
  _onSortChanged(event, target) {
    const currentUrl = new URL(location.href);
    currentUrl.searchParams.set("sort_by", target.value);
    currentUrl.searchParams.delete("page");
    syncSort(this.closest("product-facet"), target.value);
    this.open = false;
    this.dispatchEvent(new CustomEvent("facet:criteria-changed", {
      bubbles: true,
      detail: {
        url: currentUrl.toString()
      }
    }));
  }
};
customElements.define("sort-by-popover", SortByPopover);

})();
